import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import apiService from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import StripePaymentForm from './StripePaymentForm';

// Initialize Stripe with the publishable key
const stripePromise = loadStripe(`${process.env.STRIPE_PUBLISHABLE_KEY}`);

const PaymentMethodsManager = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch payment methods on component mount
  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const methods = await apiService.getPaymentMethods();
      setPaymentMethods(methods.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError(t('payment.errors.fetchMethods'));
      addToast({
        type: 'error',
        message: t('payment.errors.fetchMethods')
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodAdded = () => {
    // Refresh the payment methods list
    fetchPaymentMethods();
    // Hide the add form
    setShowAddForm(false);
  };

  const formatCardBrand = (brand) => {
    const brands = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      jcb: 'JCB',
      diners: 'Diners Club',
      unionpay: 'UnionPay'
    };
    return brands[brand.toLowerCase()] || brand;
  };

  const formatLastFour = (last4) => {
    return `•••• •••• •••• ${last4}`;
  };

  const formatExpiryDate = (month, year) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{t('payment.paymentMethods')}</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('payment.addNew')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {showAddForm ? (
            <div className="p-6 border border-gray-200 rounded-md bg-gray-50">
              <h3 className="text-lg font-medium mb-4">{t('payment.addPaymentMethod')}</h3>
              <Elements stripe={stripePromise}>
                <StripePaymentForm 
                  onSuccess={handlePaymentMethodAdded}
                  buttonText={t('payment.saveCard')}
                  processingText={t('payment.saving')}
                />
              </Elements>
              <button
                onClick={() => setShowAddForm(false)}
                className="mt-4 text-gray-600 hover:text-gray-800"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <>
              {paymentMethods.length === 0 ? (
                <div className="p-6 border border-gray-200 rounded-md bg-gray-50 text-center">
                  <p className="text-gray-600">{t('payment.noPaymentMethods')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="p-4 border border-gray-200 rounded-md flex justify-between items-center">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{formatCardBrand(method.card.brand)}</span>
                          <span className="text-gray-600">{formatLastFour(method.card.last4)}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {t('payment.expires')}: {formatExpiryDate(method.card.exp_month, method.card.exp_year)}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {method.isDefault && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            {t('payment.default')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentMethodsManager;
